import config from '../../config/config';

import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

interface UtilityCostPayload {
  energyCostEnabled: boolean;
  partnerRevenueSharePercentage: number | null;
  cpoRevenueSharePercentage: number | null;
}

interface RevenueConfigurationPercentagePayload {
  partnerRevenueSharePercentage: number;
  cpoRevenueSharePercentage: number;
  accountCustomId: string;
  stationId?: string;
}

interface AddAccountRevenueSharePayload {
  accountCustomId: string;
  stationId?: string;
  partnerRevenueSharePercentage: number;
  cpoRevenueSharePercentage: number;
}

export default {
  /**
   * Fetch the Accounts list with pagination
   * @param {any} query - The query
   */
  fetchAccounts: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit || 5 };
    return getRequest(
      `${config.subscription.accountApiUrl}api/super-admin/v1/facility-account/list-accounts`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Accounts list for export
   * @param {any} query - The query
   */
  fetchAccountsExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.subscription.accountApiUrl}api/super-admin/v1/facility-account/export-accounts`,
      { ...parameters },
    );
  },
  /**
   * Edit the account function
   * @param {any} data - The account percentage update data
   * @returns {any} A promise that resolves with the update response
   */
  editAccountPercentage: (data: any): Promise<any> => {
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/revenue-sharing`, data);
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} query - The query
   */
  fetchChargingStations: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station`, {
      ...parameters,
    });
  },
  /**
   * Fetch the charging station list for a partner
   * @param {string} partnerId - The partner id
   * @returns {Promise<any>} Promise resolving with the charging station list
   */
  fetchChargingStationsList: (partnerId: string): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/common/v1/charging-station/station-list/${partnerId}`,
    );
  },
  /**
   * Fetch the charging station list for export
   * @param {any} query - The query
   */
  fetchChargingStationsExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/export-data`, {
      ...parameters,
    });
  },
  /**
   * Fetch the charging station list with pagination
   */
  fetchAccountList: () => {
    return getRequest(
      `${config.subscription.accountApiUrl}api/super-admin/v1/facility-account/list-accounts`,
    );
  },
  /**
   * Fetch the charging station list with pagination
   * @param {any} query - The query
   */
  fetchRevenueSharing: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/charging-transaction-revenue/revenue-list`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the charging station list with pagination
   */
  fetchRevenueSharingListFilters: (): Promise<any> =>
    getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/charging-transaction-revenue/revenue-list/filter-list`,
    ),
  /**
   * Fetch the revenue management account filter list
   */
  fetchRevenueManagementAccountsListFilters: (): Promise<any> =>
    getRequest(
      `${config.subscription.accountApiUrl}api/super-admin/v1/facility-account/list-filters`,
    ),
  /**
   * Fetch the revenue management station filter list
   */
  fetchRevenueManagementStationListFilters: (): Promise<any> =>
    getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/list-filters`),
  /**
   * Fetch the revenue list for export
   * @param {any} query - The query
   */
  fetchRevenueSharingExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/charging-transaction-revenue/revenue-list/export`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the default revenue sharable percentage (utility cost defaults)
   * @returns {any} Promise resolving with the utility cost settings
   */
  fetchUtilityCost: (): Promise<any> =>
    getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/default-revenue-sharable-percentage`,
    ),
  /**
   * Update the default revenue sharable percentage (utility cost defaults)
   * @param {UtilityCostPayload} data - The utility cost settings to save
   * @returns {any} Promise resolving with the saved utility cost settings
   */
  updateUtilityCost: (data: UtilityCostPayload): Promise<any> =>
    postRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/default-revenue-sharable-percentage`,
      data,
    ),
  /**
   * Fetch the revenue configuration list (accounts/stations) with pagination
   * @param {QueryParameters} query - The query, including `type` (e.g. 'Station' or 'Account')
   * @returns {Promise<any>} Promise resolving with the revenue configuration list
   */
  fetchRevenueConfigurationList: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/revenue-list`,
      { ...parameters },
    );
  },
  /**
   * Fetch the revenue configuration list for export
   * @param {QueryParameters} query - The query, including `type` (e.g. 'Station' or 'Account')
   * @returns {Promise<any>} Promise resolving with the exportable revenue configuration list
   */
  fetchRevenueConfigurationListExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/revenue-list/export`,
      { ...parameters },
    );
  },
  /**
   * Update the revenue configuration partner/cpo share percentage for an account
   * @param {RevenueConfigurationPercentagePayload} data - The partner/cpo percentage and account custom id
   * @returns {Promise<any>} Promise resolving with the updated revenue configuration
   */
  updateRevenueConfigurationPercentage: (
    data: RevenueConfigurationPercentagePayload,
  ): Promise<any> =>
    postRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/revenue-list`,
      data,
    ),
  /**
   * Fetch the revenue configuration filter list
   * @param {QueryParameters} query - The query, including `type` (e.g. 'Station' or 'Account')
   * @returns {Promise<any>} Promise resolving with the revenue configuration filter list
   */
  fetchRevenueConfigurationListFilters: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/revenue-list/filter-list`,
      { ...parameters },
    );
  },
  /**
   * Add a new account revenue share entry
   * @param {AddAccountRevenueSharePayload} data - The account id and partner/cpo percentages
   * @returns {Promise<any>} Promise resolving with the saved revenue share entry
   */
  addAccountRevenueShare: (data: AddAccountRevenueSharePayload): Promise<any> =>
    postRequest(
      `${config.transactionApiUrl}api/super-admin/v1/revenue-configuration/revenue-list`,
      data,
    ),
};
